import {useEffect} from 'react';
import {ClientAnalytics, Cart, CartWithActions} from '@shopify/hydrogen';
import {
  TwlAnalyticsEvent,
  VisitorActivityAction,
  VisitorActivityCategory,
} from '../constants';
import {
  VisitorActivityPayloadType,
  logVisitorActivity,
} from '../backendApi/visitorActivityApi';
import Analytics from 'analytics';
import googleTagManager from '@analytics/google-tag-manager';
import {config} from '../config';
import {wisepops} from '../utils/wisepopups.client';

type AttributeHandler = (
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
) => VisitorActivityPayloadType;

type PartialVisitorActivityPayloadType = Pick<
  VisitorActivityPayloadType,
  'url'
> &
  Partial<Exclude<VisitorActivityPayloadType, 'url'>>;

const activityDataHandlerMap: Record<string, AttributeHandler> = {
  [ClientAnalytics.eventNames.PAGE_VIEW]: pageViewDataHandler,
  [TwlAnalyticsEvent.VIEWED_PRODUCT]: productViewDataHandler,
  [ClientAnalytics.eventNames.ADD_TO_CART]: addToCartDataHandler,
  [ClientAnalytics.eventNames.UPDATE_CART]: updateCartDataHandler,
  [TwlAnalyticsEvent.LIVE_SEARCH]: liveSearchDataHandler,
  [TwlAnalyticsEvent.SHOP_SEARCH]: shopSearchDataHandler,
  [TwlAnalyticsEvent.ADD_TO_WATCHLIST]: addToWatchlistDataHandler,
  [TwlAnalyticsEvent.REMOVE_FROM_WATCHLIST]: removeFromWatchlistDataHandler,
  [TwlAnalyticsEvent.ADD_TO_COLLECTION]: addToCollectionDataHandler,
  [TwlAnalyticsEvent.REMOVE_FROM_COLLECTION]: removeFromCollectionDataHandler,
  [TwlAnalyticsEvent.RATE_PRODUCT]: rateProductDataHandler,
};

function pageViewDataHandler(
  activityData: PartialVisitorActivityPayloadType,
): VisitorActivityPayloadType {
  lastPageLogged = activityData.url;

  wisepops('pageview');

  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PAGE_VIEW,
        action: VisitorActivityAction.BROWSE,
      },
    ],
  });
}

function productViewDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.BROWSE,
        product_id: analyticsData?.productId,
      },
    ],
  });
}

function addToCartDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  if (window.comet) {
    window.comet('add_to_cart');
  }

  return Object.assign(activityData, {
    events: (
      analyticsData.addedCartLines as Parameters<CartWithActions['linesAdd']>[0]
    )?.map((line) => ({
      category: VisitorActivityCategory.PRODUCT,
      action: VisitorActivityAction.ADD_TO_CART,
      product_id: line.merchandiseId,
      data_json: {quantity: line.quantity},
    })),
  });
}

function updateCartDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: (
      analyticsData.updatedCartLines as Parameters<
        CartWithActions['linesUpdate']
      >[0]
    )?.map((line) => {
      const removing = line.quantity <= 0;
      const variantId = (analyticsData?.oldCart as Cart)?.lines?.find(
        (cartLine) => cartLine.id === line.id,
      )?.merchandise?.id;
      return {
        category: VisitorActivityCategory.PRODUCT,
        action: removing
          ? VisitorActivityAction.REMOVE_FROM_CART
          : VisitorActivityAction.ADD_TO_CART,
        product_id: variantId,
        data_json: removing ? null : {quantity: line.quantity},
      };
    }),
  });
}

function liveSearchDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.SEARCH,
        action: VisitorActivityAction.SEARCH_QUERY,
        data_json: analyticsData.searchTokens
          ? {
              query: (analyticsData.searchTokens as Array<string>)
                .map((token) => `"${token}"`)
                .join(' '),
            }
          : null,
      },
    ],
  });
}

function shopSearchDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.SEARCH,
        action: VisitorActivityAction.SEARCH_QUERY,
        data_json: analyticsData.filters
          ? {query: analyticsData.filters}
          : null,
      },
    ],
  });
}

function addToWatchlistDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.TOGGLE_WATCH_TRUE,
        product_id: analyticsData.productId,
      },
    ],
  });
}

function removeFromWatchlistDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.TOGGLE_WATCH_FALSE,
        product_id: analyticsData.productId,
      },
    ],
  });
}

function addToCollectionDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.ADD_TO_COLLECTION,
        product_id: analyticsData.productId,
      },
    ],
  });
}

function removeFromCollectionDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.REMOVE_FROM_COLLECTION,
        product_id: analyticsData.productId,
      },
    ],
  });
}

function rateProductDataHandler(
  activityData: PartialVisitorActivityPayloadType,
  analyticsData: Record<string, unknown>,
): VisitorActivityPayloadType {
  return Object.assign(activityData, {
    events: [
      {
        category: VisitorActivityCategory.PRODUCT,
        action: VisitorActivityAction.ADD_RATING,
        product_id: analyticsData.productId,
        data_json: {
          new_rating: analyticsData.newRating,
          old_rating: analyticsData.oldRating,
        },
      },
    ],
  });
}

function getAnalyticsHandler(eventName: string) {
  function analyticsHandler(analyticsData: Record<string, unknown>) {
    const activityDataHandler = activityDataHandlerMap[eventName];
    if (
      !activityDataHandler ||
      (eventName === ClientAnalytics.eventNames.PAGE_VIEW &&
        lastPageLogged === analyticsData.normalizedRscUrl)
    ) {
      return;
    }
    const activityData = activityDataHandler(
      {url: analyticsData.normalizedRscUrl as string},
      analyticsData,
    );
    if ((activityData.events?.length ?? 0) > 0) {
      logVisitorActivity(activityData).catch(() => null);
    }
  }
  return analyticsHandler;
}

/* As of v0.24.0, Hydrogen's builtin PAGE_VIEW event fires whenever
 * server props are updated, hence we get duplicate events. Until this is
 * fixed (https://github.com/Shopify/hydrogen/issues/1450) we are firing
 * our own custom event whenever the window's location changes.
 */
let lastPageLogged: string;

let init = false;

interface AnalyticsListenerProps {
  sessionId: string;
}

export default function AnalyticsListener({sessionId}: AnalyticsListenerProps) {
  useEffect(() => {
    // Set up common page-specific data
    ClientAnalytics.pushToPageAnalyticsData({
      userLocale: navigator.language,
      sessionId,
    });
    if (!init) {
      // One-time initialization
      init = true;

      // Custom TWL analytics
      Object.keys(activityDataHandlerMap).forEach((eventName) => {
        ClientAnalytics.subscribe(eventName, getAnalyticsHandler(eventName));
      });

      if (config.gtmId && config.gtmId !== 'false') {
        // Google Analytics
        Analytics({
          app: 'twl3.0',
          plugins: [
            googleTagManager({
              containerId: config.gtmId,
            }),
          ],
        });
      }

      if (ClientAnalytics.hasSentFirstPageView()) {
        getAnalyticsHandler(ClientAnalytics.eventNames.PAGE_VIEW)(
          ClientAnalytics.getPageAnalyticsData(),
        );
      }
    }
  });
  return null;
}
